"use client";

import type { ComponentType } from "react";
import type { ResolvedToolDefinition } from "@/data/tools";
import type {
  SlimTool,
  SlimCategory,
} from "@/components/tools/DiscoverySections";
import dynamic from "next/dynamic";
import {
  ToolPageHeader,
  ToolIntroSection,
  ToolStepsSection,
} from "@/components/tools/shared";

// A reusable loading component to prevent layout shift while the chunk downloads
const ToolSkeleton = () => (
  <div className="w-full max-w-3xl mx-auto bg-white border border-[#e9e1ff] rounded-[1.5rem] md:rounded-[2rem] p-4 md:p-12 shadow-sm space-y-6">
    {/* Simulated Dropzone */}
    <div className="relative border-2 border-dashed border-[#e9e1ff] bg-[#faf9ff] rounded-2xl md:rounded-3xl h-64 md:h-80 flex flex-col items-center justify-center space-y-6 animate-pulse">
      {/* Fake Icon Placeholder */}
      <div className="w-16 h-16 md:w-20 md:h-20 bg-[#f3efff] rounded-2xl" />

      {/* Fake Text Placeholders */}
      <div className="space-y-3 flex flex-col items-center">
        <div className="h-5 w-48 md:w-64 bg-[#f3efff] rounded-full" />
        <div className="h-3 w-32 md:w-40 bg-[#f3efff] rounded-full opacity-70" />
      </div>
    </div>

    {/* Simulated Action Button (Staggered animation delay) */}
    <div
      className="h-[55px] md:h-[64px] w-full bg-[#f3efff] rounded-xl md:rounded-2xl animate-pulse"
      style={{ animationDelay: "150ms" }}
    />
  </div>
);

// Lazy-load sections below the fold
const ToolUseCasesSection = dynamic(() =>
  import("@/components/tools/shared").then((m) => ({
    default: m.ToolUseCasesSection,
  })),
);
const ToolAudiencesSection = dynamic(() =>
  import("@/components/tools/shared").then((m) => ({
    default: m.ToolAudiencesSection,
  })),
);
const ToolBenefitsSection = dynamic(() =>
  import("@/components/tools/shared").then((m) => ({
    default: m.ToolBenefitsSection,
  })),
);
const ToolFaqSection = dynamic(() =>
  import("@/components/tools/shared").then((m) => ({
    default: m.ToolFaqSection,
  })),
);
const DiscoverySections = dynamic(
  () => import("@/components/tools/DiscoverySections"),
);
const ImageOptimizerTool = dynamic(
  () => import("@/components/tools/ImageOptimizerTool"),
  { ssr: false, loading: ToolSkeleton },
);

const ImageConverterTool = dynamic(
  () => import("@/components/tools/ImageConverterTool"),
  { ssr: false, loading: ToolSkeleton },
);

// Define the map using next/dynamic with ssr: false for client-side only hydration
const toolComponentMap: Record<string, ComponentType> = {
  "base64-to-image": dynamic(
    () => import("@/components/tools/Base64ToImageTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "gif-maker": dynamic(() => import("@/components/tools/GIFConverterTools"), {
    ssr: false,
    loading: ToolSkeleton,
  }),
  "heic-to-jpg": dynamic(() => import("@/components/tools/HeicToImageTools"), {
    ssr: false,
    loading: ToolSkeleton,
  }),
  // "image-converter": dynamic(
  //   () => import("@/components/tools/ImageConverterTool"),
  //   { ssr: false, loading: ToolSkeleton },
  // ),
  "image-converter": () => <ImageConverterTool slug="image-converter" />,
  "image-to-png": () => <ImageConverterTool slug="image-to-png" />,
  "image-to-jpg": () => <ImageConverterTool slug="image-to-jpg" />,
  "image-to-jpeg": () => <ImageConverterTool slug="image-to-jpeg" />,
  "image-to-bmp": () => <ImageConverterTool slug="image-to-bmp" />,
  "image-to-tif": () => <ImageConverterTool slug="image-to-tif" />,
  "image-to-tiff": () => <ImageConverterTool slug="image-to-tiff" />,
  "image-to-ico": () => <ImageConverterTool slug="image-to-ico" />,
  "image-to-avif": () => <ImageConverterTool slug="image-to-avif" />,
  "image-to-webp": () => <ImageConverterTool slug="image-to-webp" />,
  "image-to-gif": () => <ImageConverterTool slug="image-to-gif" />,
  "image-to-base64": dynamic(
    () => import("@/components/tools/ImageToBase64Tools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "image-to-heic": dynamic(
    () => import("@/components/tools/ImageToHeicTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "image-analyzer": dynamic(
    () => import("@/components/tools/ImageAnalyserTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "ai-pdf-analysis": dynamic(
    () => import("@/components/tools/AIPDFAnalysisTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "image-authenticity-checker": dynamic(
    () => import("@/components/tools/ImageAuthenticityCheckerTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "image-dpi-checker": dynamic(
    () => import("@/components/tools/ImageDPICheckerTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "image-color-effects": dynamic(
    () => import("@/components/tools/ImageColorEffectTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "convert-image-to-pure-black-and-white-online": dynamic(
    () => import("@/components/tools/BlackAndWhiteImageTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "image-sharpen": dynamic(() => import("@/components/tools/ImageSharpenTools"), {
    ssr: false,
    loading: ToolSkeleton,
  }),
  "image-cropper": dynamic(() => import("@/components/tools/CropImageTools"), {
    ssr: false,
    loading: ToolSkeleton,
  }),
  "image-dpi-changer": dynamic(
    () => import("@/components/tools/ImageDPIChangerTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "image-resizer": dynamic(
    () => import("@/components/tools/ResizeImageTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "image-watermark": dynamic(
    () => import("@/components/tools/WatermarkTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "svg-image-optimizer": dynamic(
    () => import("@/components/tools/SVGImageOptimization"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "favicon-generator": dynamic(
    () => import("@/components/tools/FaviconGenerator"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "compress-pdf": dynamic(
    () => import("@/components/tools/PDFCompressorTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "extract-pdf-images": dynamic(
    () => import("@/components/tools/ExtractPDFImagesTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "image-to-pdf": dynamic(
    () => import("@/components/tools/ImageToPDFConverterTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "pdf-to-image": dynamic(
    () => import("@/components/tools/PDFToImageConverterTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "pdf-dpi-checker": dynamic(
    () => import("@/components/tools/PDFDpicheckerTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "pdf-validate": dynamic(() => import("@/components/tools/PDFValidateTools"), {
    ssr: false,
    loading: ToolSkeleton,
  }),
  "pdf-add-password": dynamic(
    () => import("@/components/tools/PDFAddPasswordTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "pdf-remove-password": dynamic(
    () => import("@/components/tools/PDFRemovePasswordTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "pdf-dpi-changer": dynamic(
    () => import("@/components/tools/PDFDpiChangerTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "remove-image-metadata": dynamic(
    () => import("@/components/tools/ExifScrubberTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "secure-password-generator": dynamic(
    () => import("@/components/tools/PasswordGeneratorTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "uuid-generator": dynamic(
    () => import("@/components/tools/UUIDGeneratorTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "text-hash-generator": dynamic(
    () => import("@/components/tools/TextHashGenerator"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "encrypt-text-online": dynamic(
    () => import("@/components/tools/EncryptTextOnlineTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "decrypt-text-online": dynamic(
    () => import("@/components/tools/DecryptTextOnlineTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "sticker-maker": dynamic(
    () => import("@/components/tools/StickerGeneratorTools"),
    { ssr: false, loading: ToolSkeleton },
  ),
  "optimize-twitter-image": () => (
    <ImageOptimizerTool slug="optimize-twitter-image" />
  ),
  "optimize-instagram-image": () => (
    <ImageOptimizerTool slug="optimize-instagram-image" />
  ),
  "optimize-youtube-thumbnail-image": () => (
    <ImageOptimizerTool slug="optimize-youtube-thumbnail-image" />
  ),
  "optimize-whatsapp-image": () => (
    <ImageOptimizerTool slug="optimize-whatsapp-image" />
  ),
  "optimize-web-image": () => <ImageOptimizerTool slug="optimize-web-image" />,
  "optimize-seo-responsive-image": () => (
    <ImageOptimizerTool slug="optimize-seo-responsive-image" />
  ),
  "optimize-image-custom": () => (
    <ImageOptimizerTool slug="optimize-image-custom" />
  ),
};

type ToolPageClientProps = {
  tool: ResolvedToolDefinition;
  allTools: SlimTool[];
  allCategories: SlimCategory[];
};

export default function ToolPageClient({
  tool,
  allTools,
  allCategories,
}: ToolPageClientProps) {
  // Directly grab the component from the map
  const ActiveToolComponent = toolComponentMap[tool.slug];

  return (
    <section className="pt-32 md:pt-36 pb-16 w-full max-w-7xl mx-auto px-4 sm:px-6 py-16 space-y-20">
      <ToolPageHeader tool={tool} />

      {/* Render it directly. Next.js handles the chunk fetching and loading state automatically */}
      {ActiveToolComponent ? (
        <ActiveToolComponent />
      ) : (
        <div className="w-full text-center p-8 bg-red-50 text-red-500 rounded-2xl">
          Tool not found or is currently unavailable.
        </div>
      )}

      <ToolIntroSection tool={tool} />
      <ToolUseCasesSection tool={tool} />
      <ToolAudiencesSection tool={tool} />
      <ToolStepsSection tool={tool} />
      <ToolBenefitsSection tool={tool} />
      <ToolFaqSection tool={tool} />

      <DiscoverySections
        currentSlug={tool.slug}
        allTools={allTools}
        allCategories={allCategories}
      />
    </section>
  );
}
